import { BotSequenceType } from "@swivl-mono/types";

export interface LadderPreview {
  /** Day 0 as an ISO date — for PAST_DUE, the due date = paidThroughDate + 1 day. */
  anchorDate: string;
  /** Whole days from the anchor to today. */
  offsetToday: number;
  /** The latest ladder step at or below today's offset; null when none is due yet. */
  dueStep: number | null;
  /** Always 0 for a run created today: a day can only fire on its own day (decision 69). */
  stalenessDays: number | null;
  /** Steps that collapse into the due step, written as `skipped:superseded`. */
  supersededSteps: number[];
  /** Plain-language outcome, or the reason nothing will book. */
  verdict: string;
  willBook: boolean;
}

function toUtcMidnight(value: Date): number {
  return Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

/**
 * What the scheduler will do with this anchor + ladder, computed client-side so an operator
 * sees "step 7, 1 day stale" BEFORE submitting rather than discovering an empty ledger later.
 *
 * Deliberately approximate in one respect: it works in UTC days, while the scheduler works in
 * the location's local day. That can differ by one near midnight — fine for a sanity check,
 * and the reason the verdict never claims more precision than "≈".
 */
export function buildLadderPreview(params: {
  sequenceType: BotSequenceType;
  /** `paidThroughDate` (PAST_DUE) or `leadCreatedDate` (ABANDONED_CART) from the diff. */
  anchorSource: string | null;
  ladderOffsetDays: number[];
  now?: Date;
}): LadderPreview | null {
  const { sequenceType, anchorSource, ladderOffsetDays } = params;
  if (!anchorSource || ladderOffsetDays.length === 0) {
    return null;
  }
  const parsed = new Date(anchorSource);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  // PAST_DUE Day 0 is the DUE date — one day after the paid-through date. Every other type
  // anchors directly on its source date.
  const anchorMs = toUtcMidnight(parsed) + (sequenceType === BotSequenceType.PAST_DUE ? 24 * 60 * 60 * 1000 : 0);
  const todayMs = toUtcMidnight(params.now ?? new Date());
  const offsetToday = Math.round((todayMs - anchorMs) / (24 * 60 * 60 * 1000));

  const sorted = [...new Set(ladderOffsetDays)].sort((left, right) => left - right);
  const anchorDate = new Date(anchorMs).toISOString().slice(0, 10);

  // ENG-3216 decision 69: the run creates its enrollment TODAY, and a ladder day already past
  // when an enrollment is created is missed — it never fires, not even "Always send on this day".
  // So the only day that can fire from this run is a ladder day landing on today.
  const missedSteps = sorted.filter((step) => step < offsetToday);
  const dueStep = sorted.includes(offsetToday) ? offsetToday : null;
  const supersededSteps: number[] = [];

  if (dueStep === null) {
    const next = sorted.find((step) => step > offsetToday);
    const missed = missedSteps.length > 0 ? ` Days ${missedSteps.join(", ")} were already past — they are recorded as missed and never fire.` : "";
    return {
      anchorDate,
      offsetToday,
      dueStep: null,
      stalenessDays: null,
      supersededSteps,
      verdict:
        next !== undefined
          ? `Nothing fires today — Day 0 is ${anchorDate}, today is ≈ offset ${offsetToday}, and no ladder day lands on it.${missed} Day ${next} is next, in ${next - offsetToday} ${next - offsetToday === 1 ? "day" : "days"}.`
          : `Nothing fires — Day 0 is ${anchorDate}, today is ≈ offset ${offsetToday}, and the ladder has no day on or after it.${missed}`,
      willBook: false
    };
  }

  const stalenessDays = 0;
  const missed = missedSteps.length > 0 ? ` Days ${missedSteps.join(", ")} were already past — recorded as missed, never fired.` : "";
  return {
    anchorDate,
    offsetToday,
    dueStep,
    stalenessDays,
    supersededSteps,
    verdict: `Day 0 is ${anchorDate}; today is ≈ offset ${offsetToday}, so Day ${dueStep} fires today.${missed}`,
    willBook: true
  };
}
